import { BaseQueryApi } from '@reduxjs/toolkit/dist/query'
import { AxiosRequestConfig } from 'axios'
import { getDeviceId } from 'lib/user'
import { PhoneVerificationRequestsResponse, PresignedUrl } from 'lib/_types'
import { axiosBaseQuery } from 'store/libs/baseQuery'

import { yayApi } from '../baseApi'

const fetchWithPresignedUrl = (
  api: BaseQueryApi,
  { url, params },
  axiosConfig?: AxiosRequestConfig
) =>
  axiosBaseQuery({ client: 'default' })(
    {
      url: `/v1/id_check${url}`,
      params: {
        app_name: 'yay_staging',
        ...params,
      },
    },
    api
  ).then((res: { data: PresignedUrl }) =>
    axiosBaseQuery({ client: 'free' })(
      { url: res?.data?.presigned_url, ...axiosConfig },
      api
    )
  )

export const idCardCheckApi = yayApi
  .enhanceEndpoints({
    addTagTypes: ['IDCardCheck/Verification'],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      checkPhoneVerification: build.query({
        providesTags: ['IDCardCheck/Verification'],
        async queryFn(_args, baseQueryApi, _extraOptions, baseQuery) {
          let status = 'pending'

          const phoneVerificationRequestsResponse = (await baseQuery({
            url: '/v1/users/phone_verification_requests',
          })) as { data: PhoneVerificationRequestsResponse }

          if (
            phoneVerificationRequestsResponse?.data.phone_verification_request
              ?.status === 'success'
          ) {
            return {
              data: {
                status: 'verified',
              },
            }
          }

          const checkAuuidResponse = await fetchWithPresignedUrl(baseQueryApi, {
            url: '/users/show',
            params: {
              auuid: getDeviceId(),
            },
          }).then((res) => (res?.error ? res : res?.data))

          if (checkAuuidResponse?.error?.status === 404) {
            await fetchWithPresignedUrl(
              baseQueryApi,
              {
                url: '/users/create',
                params: {
                  auuid: getDeviceId(),
                },
              },
              {
                method: 'POST',
                data: {
                  auuid: getDeviceId(),
                },
              }
            )
          }

          if (checkAuuidResponse?.data?.attributes?.mobile_number) {
            const refreshResponse = await axiosBaseQuery({ client: 'yayJWT' })(
              {
                url: '/v2/users/refresh_phone_verification',
                method: 'POST',
                data: {
                  auuid: getDeviceId(),
                },
              },
              baseQueryApi
            )

            if (refreshResponse?.data?.result === 'success') {
              return {
                data: {
                  status: 'verified',
                },
              }
            }
          }

          return {
            data: {
              status,
            },
          }
        },
      }),

      submitPhoneVerification: build.mutation({
        invalidatesTags: ['IDCardCheck/Verification'],

        async queryFn({ mobile_number }, baseQueryApi) {
          return fetchWithPresignedUrl(
            baseQueryApi,
            {
              url: '/phone_verifications/create',
              params: {
                auuid: getDeviceId(),
                uuid: getDeviceId(),
                mobile_number,
              },
            },
            {
              method: 'POST',
              data: {
                mobile_number,
                uuid: getDeviceId(),
              },
            }
          )
        },
      }),

      requestNewCode: build.query({
        keepUnusedDataFor: 0,

        async queryFn({ type }, baseQueryApi) {
          return fetchWithPresignedUrl(baseQueryApi, {
            url: '/phone_verifications/send',
            params: {
              uuid: getDeviceId(),
              type,
            },
          })
        },
      }),

      submitVerificationCode: build.mutation({
        invalidatesTags: ['IDCardCheck/Verification'],

        async queryFn({ code }, baseQueryApi) {
          return fetchWithPresignedUrl(
            baseQueryApi,
            {
              url: '/phone_verifications/submit',
              params: {
                uuid: getDeviceId(),
                code,
              },
            },
            {
              method: 'POST',
              data: {
                uuid: getDeviceId(),
                code,
              },
            }
          )
        },
      }),
    }),
    overrideExisting: false,
  })

export const {
  useCheckPhoneVerificationQuery,
  useSubmitPhoneVerificationMutation,
  useSubmitVerificationCodeMutation,
  useRequestNewCodeQuery,
  useLazyRequestNewCodeQuery,
} = idCardCheckApi
